/**
 * What the player is playing with (docs/spec/04 §7): which raw keys a screen may take for itself,
 * and whether the player is on touch controls.
 */

import type { KeyInput } from '../scene';

/**
 * Keys that belong to the browser or the system (reload, the developer tools, the OS menu): the
 * game never takes them, even while it waits for "any key".
 */
export function isBrowserKey(code: string): boolean {
  return /^F\d{1,2}$/.test(code) || /^(Meta|OS)/.test(code) || code === 'ContextMenu';
}

/** A key press a screen should leave alone: a browser key, or anything with Ctrl or Cmd held. */
export function notOurs(k: KeyInput): boolean {
  return k.ctrl || isBrowserKey(k.code);
}

/** The device's main pointer is a finger (no DOM, as in tests: no). */
function coarsePointer(): boolean {
  try {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(pointer: coarse)').matches || 'ontouchstart' in window;
  } catch {
    return false;
  }
}

/**
 * The player will be on touch controls in a game. `showing` is `services.input.touch()`, which is
 * only true while the overlay is up - and it is hidden in the menus - so the Touch controls setting
 * is asked as well: ON, or AUTO on a device whose pointer is a finger.
 */
export function usesTouch(showing: boolean, setting: 'auto' | 'on' | 'off'): boolean {
  return showing || setting === 'on' || (setting === 'auto' && coarsePointer());
}

/** Shift, Ctrl or Alt on its own: not "any key". */
export function isModifier(code: string): boolean {
  return /^(Shift|Control|Alt)/.test(code);
}
